import { Link } from 'react-router-dom';
import { useProducts } from '../hooks/useProducts';
import ProductCard from '../components/ProductCard';
import { useEffect, useRef } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import heroBg from '../assets/banner.jpg';
import slideImg from '../assets/slide-image.jpg';
import imgRings from '../assets/rings.jpg';
import imgBracelets from '../assets/bracelets.jpg';
import missionImg from '../assets/mission.jpg';

/* clamp-based responsive values (no JS needed for medium screens) */
const H_PAD = 'clamp(60px, 12.5vw, 240px)'; // 60px@480 → 240px@1920
const V_PAD = 'clamp(60px, 8vw, 120px)';    // 60px@750 → 120px@1500

function useReveal() {
  const ref = useRef();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.style.opacity = '1'; el.style.transform = 'translateY(0)'; }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

function SkeletonCard() {
  const bar = (w, extra) => ({
    height: '13px', width: w, background: '#eee',
    animation: 'norelio-pulse 1.4s ease-in-out infinite', ...extra,
  });
  return (
    <div>
      <div style={{ aspectRatio: '1/1', background: '#eee', marginBottom: '14px', animation: 'norelio-pulse 1.4s ease-in-out infinite' }} />
      <div style={bar('65%', { marginBottom: '10px' })} />
      <div style={bar('35%')} />
    </div>
  );
}

function Section({ title, products, isMobile, loading }) {
  const ref = useReveal();
  const items = loading ? Array.from({ length: 3 }) : products;
  return (
    <section style={{
      /* On mobile: 24px left/right padding so the title is indented.
         The slider escapes it via negative margin then re-adds padding on the inner div. */
      padding: isMobile ? `60px 24px 0` : `${V_PAD} ${H_PAD} 0`,
      opacity: 0, transform: 'translateY(30px)', transition: 'all 0.7s ease',
    }} ref={ref}>
      <h2 style={{
        fontFamily: "'Josefin Sans', sans-serif",
        fontSize: '18px', letterSpacing: '4px', textTransform: 'uppercase',
        fontWeight: 300, marginBottom: '40px', color: '#111',
      }}>{title}</h2>

      {isMobile ? (
        /*
          Two-div pattern to get real left padding in a horizontal scroll on WebKit:
          - outer div: scrolls, bleeds to viewport edges via negative margin
          - inner div: NOT overflow, so paddingLeft IS respected by all browsers
        */
        <div style={{
          overflowX: 'auto',
          marginLeft: '-24px',
          marginRight: '-24px',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}>
          <div style={{
            display: 'inline-flex',
            gap: '16px',
            paddingLeft: '24px',
            paddingRight: '24px',
          }}>
            {items.map((p, i) => (
              <div key={p?.id ?? i} style={{ width: '72vw', flexShrink: 0 }}>
                {loading ? <SkeletonCard /> : <ProductCard product={p} />}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '32px' }}>
          {items.map((p, i) => loading ? <SkeletonCard key={i} /> : <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </section>
  );
}

export default function Home() {
  const { products, loading } = useProducts();
  const isMobile = useIsMobile();
  const bracelets = products.filter(p => p.category === 'Bracelets').slice(0, 3);
  const bagues = products.filter(p => p.category === 'Bagues').slice(0, 3);

  const heroRef = useRef();
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    setTimeout(() => { el.style.opacity = '1'; }, 100);
  }, []);

  return (
    <main>
      {/* Hero */}
      <section style={{
        position: 'relative', height: '45vh', minHeight: '280px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          filter: 'brightness(0.45)',
        }} />
        <div ref={heroRef} style={{
          position: 'relative', textAlign: 'center',
          opacity: 0, transition: 'opacity 1.2s ease',
          padding: '0 20px',
        }}>
          <p style={{
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: isMobile ? '13px' : '18px',
            letterSpacing: isMobile ? '3px' : '5px',
            textTransform: 'uppercase', color: '#fff', marginBottom: '28px', fontWeight: 200,
          }}>Puissance et raffinement à votre poignet.</p>
          <Link to="/catalogue" style={{
            display: 'inline-block', padding: '13px 36px',
            border: '1px solid rgba(255,255,255,0.8)', color: '#fff',
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: '11px', letterSpacing: '3px', textTransform: 'uppercase',
            textDecoration: 'none', fontWeight: 300, transition: 'all 0.3s',
          }}
            onMouseEnter={e => { e.target.style.background = '#fff'; e.target.style.color = '#111'; }}
            onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = '#fff'; }}
          >Visiter le catalogue</Link>
        </div>
      </section>

      {/* Products */}
      {(loading || bracelets.length > 0) && <Section title="Votre Signature." products={bracelets} isMobile={isMobile} loading={loading} />}
      {(loading || bagues.length > 0) && <Section title="Votre Empreinte" products={bagues} isMobile={isMobile} loading={loading} />}

      {/* Full-width slide image */}
      <div style={{ width: '100%', lineHeight: 0, margin: isMobile ? '60px 0' : `${V_PAD} 0` }}>
        <img src={slideImg} alt="" style={{ width: '100%', display: 'block', objectFit: 'cover' }} />
      </div>

      {/* Category links */}
      <section style={{ padding: isMobile ? '0 20px' : `0 ${H_PAD}` }}>
        <h2 style={{
          fontFamily: "'Josefin Sans', sans-serif",
          fontSize: '18px', letterSpacing: '4px', textTransform: 'uppercase',
          fontWeight: 300, marginBottom: '32px', color: '#111',
        }}>Sélections Exclusives</h2>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '20px' : '24px' }}>
          {[['Bagues', imgRings], ['Bracelets', imgBracelets]].map(([cat, img]) => (
            <Link key={cat} to={`/catalogue?cat=${cat}`} style={{ textDecoration: 'none', color: '#111', display: 'block' }}>
              <div style={{ overflow: 'hidden', aspectRatio: '4/3', marginBottom: '14px' }}>
                <img src={img} alt={cat} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.6s ease' }}
                  onMouseEnter={e => e.target.style.transform = 'scale(1.04)'}
                  onMouseLeave={e => e.target.style.transform = 'scale(1)'}
                />
              </div>
              <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '16px', letterSpacing: '2px' }}>{cat} →</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Mission */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        alignItems: 'center',
        padding: isMobile ? '60px 20px' : `${V_PAD} ${H_PAD}`,
        gap: isMobile ? '40px' : '0',
      }}>
        <div style={{ paddingRight: isMobile ? '0' : '60px' }}>
          <h2 style={{
            fontFamily: "'Josefin Sans', sans-serif",
            fontSize: '18px', letterSpacing: '4px', textTransform: 'uppercase',
            fontWeight: 300, marginBottom: '28px',
          }}>Notre Mission</h2>
          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '17px', lineHeight: '1.9', color: '#333', marginBottom: '20px' }}>
            Chez Norelio, nous sommes passionnés par l'art de la bijouterie masculine. Notre mission est de proposer des pièces uniques, élégantes et durables, conçues pour refléter la personnalité de chaque homme.
          </p>
          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '17px', lineHeight: '1.9', color: '#333' }}>
            Nous croyons que chaque bijou raconte une histoire, et nous nous engageons à offrir des créations de qualité qui sauront accompagner les moments les plus précieux de votre vie.
          </p>
        </div>
        <div>
          <img src={missionImg} alt="Norelio Mission" style={{ width: '100%', display: 'block' }} />
        </div>
      </section>
    </main>
  );
}
